import { useEffect, useState } from 'react';
import { api } from '../api/client';

type Etat = 'inconnu' | 'hors-standalone' | 'non-supporte' | 'a-activer' | 'refuse' | 'active';

function estStandalone(): boolean {
  // Sur iOS, `navigator.standalone` est le SEUL signal fiable, et il fait
  // autorite des qu'il existe.
  //
  // La media query `display-mode: standalone` ne peut pas servir de secours
  // ici : dans Safari iOS elle vaut true des que le manifest declare
  // `display: standalone`, que la page soit installee ou non — WebKit repond
  // d'apres le manifest, pas d'apres le mode d'affichage reel. Les combiner
  // avec un OU faisait croire a une app installee en pleine navigation
  // Safari, et menait a afficher « notifications bloquees » a quelqu'un qui
  // n'avait simplement pas encore ajoute l'app a son ecran d'accueil.
  const ios = (navigator as unknown as { standalone?: boolean }).standalone;
  if (typeof ios === 'boolean') return ios;

  // Ailleurs (Android, bureau), la media query est correcte.
  return window.matchMedia('(display-mode: standalone)').matches;
}

function supporte(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** La cle VAPID circule en base64url ; l'API push veut des octets bruts. */
function versOctets(base64url: string): ArrayBuffer {
  const b64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const brut = atob(b64);
  const tampon = new ArrayBuffer(brut.length);
  const vue = new Uint8Array(tampon);
  for (let i = 0; i < brut.length; i++) vue[i] = brut.charCodeAt(i);
  return tampon;
}

/**
 * Activation des notifications.
 *
 * Trois contraintes iOS, toutes bloquantes :
 *  - il faut iOS 16.4 ou plus recent ;
 *  - l'app doit avoir ete ajoutee a l'ecran d'accueil (dans Safari, rien ne
 *    fonctionne, et sans message d'erreur) ;
 *  - requestPermission doit partir d'un vrai geste utilisateur, d'ou ce bouton
 *    plutot qu'un appel automatique au chargement.
 */
export function ActiverNotifs() {
  const [etat, setEtat] = useState<Etat>('inconnu');
  const [occupe, setOccupe] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!supporte()) {
      setEtat(estStandalone() ? 'non-supporte' : 'hors-standalone');
      return;
    }
    if (!estStandalone()) {
      setEtat('hors-standalone');
      return;
    }
    if (Notification.permission === 'denied') {
      setEtat('refuse');
      return;
    }
    void navigator.serviceWorker.ready.then(async (reg) => {
      const abonnement = await reg.pushManager.getSubscription();
      setEtat(abonnement && Notification.permission === 'granted' ? 'active' : 'a-activer');
    });
  }, []);

  async function activer() {
    setOccupe(true);
    setMessage(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setEtat(permission === 'denied' ? 'refuse' : 'a-activer');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const { cle_publique } = await api.clePush();
      const abonnement =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          // Obligatoire sur iOS : un abonnement sans payload chiffre est
          // refuse par le service d'Apple.
          userVisibleOnly: true,
          applicationServerKey: versOctets(cle_publique),
        }));

      const json = abonnement.toJSON();
      if (!json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('Abonnement incomplet.');
      }
      await api.abonner(abonnement.endpoint, { p256dh: json.keys.p256dh, auth: json.keys.auth });
      setEtat('active');
      setMessage('Notifications activees. Le brief arrivera a 6 h 30.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Echec de l activation.');
    } finally {
      setOccupe(false);
    }
  }

  // Une fois les notifications actives, la carte n'a plus rien a dire : elle
  // occuperait le bas de l'ecran d'accueil tous les matins pour rappeler un
  // reglage deja fait. Le bouton de test disparait avec elle ; en cas de
  // doute, POST /api/push/test reste disponible.
  if (etat === 'inconnu' || etat === 'active') return null;

  return (
    <div className="carte-notifs">
      {etat === 'hors-standalone' && (
        <>
          <h3>Recevoir le brief a 6 h 30</h3>
          <p>
            iOS n envoie de notifications qu aux apps ajoutees a l ecran d accueil. Depuis Safari :
          </p>
          <ol>
            <li>Bouton Partager, en bas de l ecran</li>
            <li>Sur l ecran d accueil</li>
            <li>Ouvrir CamEllie depuis l icone, puis revenir ici</li>
          </ol>
        </>
      )}

      {etat === 'non-supporte' && (
        <>
          <h3>Notifications indisponibles</h3>
          <p>Cet appareil ne gere pas le Web Push. Sur iPhone, il faut iOS 16.4 ou plus recent.</p>
        </>
      )}

      {etat === 'refuse' && (
        <>
          <h3>Notifications bloquees</h3>
          <p>
            Reglages, Notifications, CamEllie : autoriser, puis revenir ici. Le navigateur ne
            redemandera pas de lui-meme.
          </p>
        </>
      )}

      {etat === 'a-activer' && (
        <>
          <h3>Recevoir le brief a 6 h 30</h3>
          <p>Une seule notification par jour, jamais une par tache.</p>
          <button type="button" className="bouton primaire pleine-largeur" onClick={activer} disabled={occupe}>
            {occupe ? 'Activation...' : 'Activer les notifications'}
          </button>
        </>
      )}

      {message && <p style={{ marginTop: 'var(--r3)', marginBottom: 0 }}>{message}</p>}
    </div>
  );
}
